<script setup>
import { ref, watch, computed } from 'vue';

const props = defineProps({
  min: { type: Number, default: 0 },
  max: { type: Number, default: 9999999999 },
  valueFrom: { type: Number, default: null },
  valueTo: { type: Number, default: null },
});

const emit = defineEmits(['change']);

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const uiPriceFromInput = ref(props.valueFrom ?? props.min);
const uiPriceToInput = ref(props.valueTo ?? props.max);
const uiPriceRange = ref([
  props.valueFrom ?? props.min,
  props.valueTo ?? props.max,
]);

const filterOpen = ref({ price: true });
const toggleFilterOpen = (key) =>
  (filterOpen.value[key] = !filterOpen.value[key]);

const getAppliedRange = () => {
  const fromRaw =
    typeof props.valueFrom === 'number' ? props.valueFrom : props.min;
  const toRaw = typeof props.valueTo === 'number' ? props.valueTo : props.max;

  const from = clamp(fromRaw, props.min, props.max);
  const to = clamp(toRaw, props.min, props.max);

  return from <= to ? [from, to] : [to, from];
};

watch(
  [
    () => props.min,
    () => props.max,
    () => props.valueFrom,
    () => props.valueTo,
  ],
  () => {
    const [from, to] = getAppliedRange();
    uiPriceRange.value = [from, to];
    uiPriceFromInput.value = from;
    uiPriceToInput.value = to;
  },
  { immediate: true },
);

const applyPriceInputs = () => {
  const fromRaw = uiPriceFromInput.value;
  const toRaw = uiPriceToInput.value;

  const from =
    fromRaw == null || fromRaw === ''
      ? props.min
      : clamp(Number(fromRaw), props.min, props.max);
  const to =
    toRaw == null || toRaw === ''
      ? props.max
      : clamp(Number(toRaw), props.min, props.max);

  const nextFrom = from <= to ? from : to;
  const nextTo = from <= to ? to : from;

  if (nextFrom !== props.valueFrom || nextTo !== props.valueTo) {
    emit('change', { from: nextFrom, to: nextTo });
  }
};

const applyPriceOnRelease = (e) => {
  const next = e?.value ?? uiPriceRange.value;
  const [from, to] = next;

  if (from !== props.valueFrom || to !== props.valueTo) {
    emit('change', { from, to });
  }
};
</script>

<template>
  <template v-if="max && min !== undefined">
    <div class="catalog__filter-top">
      <div
        class="catalog__filter-header"
        :class="{ 'catalog__filter-header--expanded': filterOpen.price }"
        @click="toggleFilterOpen('price')"
      >
        <p class="mb-0 catalog__filter-title h6-mobile">
          {{ $t('filters.price') }}
        </p>
        <button
          type="button"
          class="catalog__filter-toggle js-button-filter-price"
          :aria-expanded="filterOpen.price"
        >
          <CIcon
            :class="[
              'catalog__filter-toggle-icon',
              filterOpen.price ? 'catalog__filter-toggle-icon--rotated' : '',
            ]"
            name="direction-black"
            width="16"
            height="16"
          />
        </button>
      </div>
    </div>

    <div v-if="filterOpen.price" class="catalog__filter-content">
      <div class="catalog__filter-price">
        <div class="catalog__filter-price-inputs">
          <div class="catalog__filter-price-group">
            <FloatLabel variant="on" class="floatlabel-onwhite">
              <InputNumber
                v-model="uiPriceFromInput"
                :min="min"
                :max="max"
                :format="false"
                class="catalog__filter-price-input"
                @blur="applyPriceInputs"
                @keydown.enter="applyPriceInputs"
              />
              <label class="catalog__filter-price-input-label">
                {{ $t('filters.from') }}
              </label>
            </FloatLabel>
          </div>

          <div class="catalog__filter-price-divider"></div>

          <div class="catalog__filter-price-group">
            <FloatLabel variant="on" class="floatlabel-onwhite">
              <InputNumber
                v-model="uiPriceToInput"
                :min="min"
                :max="max"
                :format="false"
                class="catalog__filter-price-input"
                @blur="applyPriceInputs"
                @keydown.enter="applyPriceInputs"
              />
              <label class="catalog__filter-price-input-label">
                {{ $t('filters.to') }}
              </label>
            </FloatLabel>
          </div>
        </div>

        <div class="catalog__filter-price-slider-container">
          <Slider
            v-model="uiPriceRange"
            :min="min"
            :max="max"
            :step="10"
            range
            class="catalog__filter-price-slider"
            @slideend="applyPriceOnRelease"
          />
        </div>
      </div>
    </div>
  </template>
</template>

<style scoped lang="scss">
.catalog__filter {
  &-top {
    background-color: $white;
    padding: 24px 16px 12px;
    position: sticky;
    top: 65px;
    z-index: 1;
    width: 100%;
    @include media-breakpoint-up(md) {
      top: 58px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-top-width, 1);
      @include responsive-spacing(padding-bottom, 12);
      @include responsive-spacing(padding-top, 24);
      @include responsive-spacing(padding-left, 16);
      @include responsive-spacing(padding-right, 16);
      @include responsive-spacing(top, 58);
    }
  }
  &-header {
    align-items: center;
    cursor: pointer;
    display: grid;
    justify-content: space-between;
    grid-template-columns: auto 16px;
    padding: 0 8px;
    @include media-breakpoint-up(xl) {
      grid-template-columns: auto px-to-vw(16);
      @include responsive-spacing(padding-left, 8);
      @include responsive-spacing(padding-right, 8);
    }
  }
  &-title {
    color: $black;
  }
  &-link {
    display: none;
  }
  &-toggle {
    cursor: pointer;
    padding: 0;
    &-icon {
      height: auto;
      transform: rotate(90deg);
      transition: transform 0.3s ease;
      width: 100%;
      &--rotated {
        transform: rotate(-90deg);
      }
    }
  }
  &-content {
    border-bottom: 1px solid $divider;
    padding: 12px 16px 16px;
    width: 100%;
    @include media-breakpoint-up(md) {
      &:last-child {
        border-bottom: unset;
      }
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-bottom-width, 1);
      @include responsive-spacing(padding-bottom, 16);
      @include responsive-spacing(padding-top, 12);
      @include responsive-spacing(padding-left, 16);
      @include responsive-spacing(padding-right, 16);
    }
  }
  &-price {
    display: flex;
    flex-direction: column;
    gap: 32px;
    padding: 7px 8px 12px 8px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 32);
      @include responsive-spacing(padding-bottom, 12);
      @include responsive-spacing(padding-top, 7);
      @include responsive-spacing(padding-left, 8);
      @include responsive-spacing(padding-right, 8);
    }
    &-inputs {
      display: grid;
      grid-template-columns: 5fr 4px 5fr;
      gap: 8px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 8);
        grid-template-columns: 5fr px-to-vw(4) 5fr;
      }
    }
    &-input {
      width: 100%;
    }
    &-divider {
      position: relative;
      &:before {
        background-color: $divider;
        bottom: 27px;
        content: '';
        position: absolute;
        height: 1px;
        width: 100%;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(bottom, 27);
          @include responsive-spacing(height, 1);
        }
      }
    }
    &-slider {
      width: 100%;
      &-container {
        padding: 0 8px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(padding-left, 8);
          @include responsive-spacing(padding-right, 8);
        }
      }
    }
  }
}
</style>
